import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import SecondStepModal from './second-step-modal.component';
import { AiOutlineClose } from 'react-icons/ai';
import { GenericModal } from '..';
import { CompleteProfileModalProps, CompleteProfileFormData, CompleteProfileFormErrors } from './types';
import { submitUserOnboarding } from '../../../features/signup/signup.slice';
import {  renderCountryOptionsWithI18n } from '../../../utils/countries';
import './complete-profile-modal.style.scss';
import { useTranslation } from 'react-i18next';
import { getItem } from '../../../helper/localstorage.helper';

interface RootState {
  auth: {
    user: {
      userId: number;
      firstName: string;
      lastName: string;
      email: string;
      phoneNo: string;
      address: string;
      zipCode: string;
      country: string;
      accountType: number;
      [key: string]: any;
    } | null;
  };
}

const CompleteProfileModal: React.FC<CompleteProfileModalProps> = ({
  show,
  onClose,
  onNext,
  onSubmit,
  onStartTour,
  isFromInvoice = false,
  initialCountry,
}) => {
  const [formData, setFormData] = useState<CompleteProfileFormData>({
    personalId: '',
    noIdNumber: false,
    dateOfBirth: '',
    city: '',
    country: '',
    profession: '',
    address: '',
    postalCode: ''
  });
  const [formErrors, setFormErrors] = useState<CompleteProfileFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [DOBValue, setDOBValue] = useState('');
  const [showSecondModal, setShowSecondModal] = useState(false);
  const [customCity, setCustomCity] = useState('');
  const user = useSelector((state: RootState) => state.auth.user);
  const localstorageUser = JSON.parse(getItem('user') as string)
  const isLoginWithBankId = localstorageUser?.isLoginWithBankId || false;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (show && initialCountry) {
      setFormData(prev => ({ ...prev, country: initialCountry }));
    }
  }, [show, initialCountry]);

  const resetForm = () => {
    setFormData({
      personalId: '',
      noIdNumber: false,
      dateOfBirth: '',
      city: '',
      country: '',
      profession: '',
      address: '',
      postalCode: ''
    });
    setFormErrors({});
    setDOBValue('');
    setCustomCity('');
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      if (field === 'noIdNumber' && !value) {
        newData.dateOfBirth = '';
      }
      return newData;
    });

    if (formErrors[field as keyof CompleteProfileFormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const baseFields = ['city', 'country', 'profession'] as const;
    const baseMessages = ['CITY_REQUIRED', 'COUNTRY_REQUIRED', 'PROFESSION_REQUIRED'];
    
    // Add email, address, and postalCode to required fields if user is logged in with BankID
    const fields = isLoginWithBankId ? [...baseFields, 'email' as const, 'address' as const, 'postalCode' as const] : baseFields;
    const messages = isLoginWithBankId ? [...baseMessages, 'EMAIL_REQUIRED', 'ADDRESS_REQUIRED', 'POSTAL_CODE_REQUIRED'] : baseMessages;
    
    const errors: CompleteProfileFormErrors = {};

    fields.forEach((field, i) => {
      if (field === 'city' && formData.city === 'other') {
        if (!customCity?.trim()) errors[field] = messages[i];
      } else if (!formData[field]?.trim()) {
        errors[field] = messages[i];
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) {
      toast.error(t('PLEASE_FILL_ALL_REQUIRED_FIELDS') as string);
      return;
    }

    if (!user) {
      toast.error(t('USER_NOT_FOUND') as string);
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        firstName: user?.firstName,
        email: isLoginWithBankId ? formData.email : user.email,
        address: formData.address || user.address,
        accountType: user.accountType,
        ssn: user.ssn,
        zipCode: formData.postalCode || user.zipCode,
        dateOfBirth: user.dateOfBirth || "" ,
        password: user.password,
        phoneNo: user.phoneNo,
        city: formData.city === 'other' ? customCity : formData.city,
        country: formData.country,
        profession: formData.profession,
      };

      const response = await (dispatch as any)(submitUserOnboarding(payload as any));

      if (response.meta.requestStatus === 'fulfilled') {
        toast.success(t('PROFILE_UPDATED_SUCCESSFULLY') as string);

        if (onSubmit) {
          onSubmit(formData);
        }

        if (isFromInvoice) {
          // From invoice flow: just close the modal, no second onboarding step
          onClose();
        } else {
          if (onNext) {
            onNext();
          } else {
            onClose();
          }
          setShowSecondModal(true);
        }
      } else {
        toast.error(response.payload || t('PROFILE_UPDATE_FAILED') as string);
      }
    } catch (error: any) {
      console.error('Profile update error:', error);

      if (error?.response?.data?.message) {
        toast.error(String(t(error.response.data.message as string)) || String(error.response.data.message));
      } else if (error?.message) {
        toast.error(String(t(error.message)));
      } else {
        toast.error(t('PROFILE_UPDATE_FAILED') as string);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const isFormComplete =
    formData.city?.trim() !== '' &&
    formData.country?.trim() !== '' &&
    formData.profession?.trim() !== '' &&
    (isLoginWithBankId ? formData.email?.trim() !== '' : true) &&
    (isLoginWithBankId ? formData.address?.trim() !== '' : true) &&
    (isLoginWithBankId ? formData.postalCode?.trim() !== '' : true) 

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className='complete-profile-container-main'> 
      <GenericModal
        show={show}
        onClose={handleClose}
        size="xl"
        showFooter={false}
        showCloseButton={false}
        closeOnBackdrop={true}
        className="complete-profile-modal"
      >
        <div className="complete-profile-content">
          {/* Header */}
          <div className="complete-profile-header">
            <h2 className="complete-profile-title">{t('COMPLETE_PROFILE')}</h2>
            <button
              className="complete-profile-close-btn"
              onClick={handleClose}
              aria-label="Close"
              disabled={isLoading}
            >
              <AiOutlineClose size={32} />
            </button>
          </div>

          <div className="complete-profile-body">
            <div className="form-section">
              {isLoginWithBankId && (
                <div className="form-group">
                  <label className="form-label">{t('EMAIL')}</label>
                  {formErrors.email && (
                    <span className="error-message">{t(formErrors.email)}</span>
                  )}
                  <input
                    type="email"
                    className="form-input py-0 my-0"
                    placeholder={t('ENTER_EMAIL') || 'Enter your email'}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={isLoading}
                    style={{ marginTop: '8px' }}
                  />
                </div>
              )}
              <div className="form-row">


                <div className="form-group">
                  <label className="form-label">{t('COUNTRY')}</label>
                  <select
                    className={`form-select ${formErrors.country ? 'error' : ''}`}
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">{t('SELECT_YOUR_COUNTRY')}</option>
                    <option value={"Sweden"}>{"Sweden"}</option>
                    <option value={"USA"}>{"USA"}</option>
                    {renderCountryOptionsWithI18n(t)}
                  </select>
                  {formErrors.country && (
                    <span className="error-message">{t(formErrors.country)}</span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">{t('CITY')}</label>
                  {formErrors.city && (
                    <span className="error-message">{t(formErrors.city)}</span>
                  )}
                  <input
                    type="text"
                    className="form-input py-0 my-0"
                    placeholder={t('ENTER_CITY') || 'Enter your city'}
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    disabled={isLoading}
                    style={{ marginTop: '8px' }}
                  />
                </div>
              </div>

              {/* Address and Postal Code Row - Only for BankID users */}
              {isLoginWithBankId && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('ADDRESS')}</label>
                    {formErrors.address && (
                      <span className="error-message">{t(formErrors.address)}</span>
                    )}
                    <input
                      type="text"
                      className="form-input py-0 my-0"
                      placeholder={t('ENTER_ADDRESS') || 'Enter your address'}
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      disabled={isLoading}
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('POSTAL_CODE')}</label>
                    {formErrors.postalCode && (
                      <span className="error-message">{t(formErrors.postalCode)}</span>
                    )}
                    <input
                      type="text"
                      className="form-input py-0 my-0"
                      placeholder={t('ENTER_POSTAL_CODE') || 'Enter your postal code'}
                      value={formData.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      disabled={isLoading}
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                </div>
              )}

              {/* Profession */}
              <div className="form-group profession-group">
                <label className="form-label">{t('PROFESSION')}</label>
                {formErrors.profession && (
                  <span className="error-message">{t(formErrors.profession)}</span>
                )}
                  <input
                    type="text"
                  className={`form-input ${formErrors.profession ? 'error' : ''}`}
                    placeholder={t('ENTER_PROFESSION') || 'Enter your profession'}
                    value={formData.profession}
                  onChange={(e) => handleInputChange('profession', e.target.value)}
                    disabled={isLoading}
                    style={{ marginTop: '8px' }}
                  />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="complete-profile-footer">
            {/* Progress Dots */}
            <div className="progress-dots">
              <div className="dot"></div>
              <div className="dot active"></div>
              <div className="dot"></div>
              {/* <div className="dot"></div> */}
            </div>

            {/* Next / Save Button */}
            <button
              className={isFromInvoice ? 'profile-save-btn' : 'profile-next-btn'}
              onClick={handleNext}
              disabled={!isFormComplete || isLoading}
            >
              {isLoading ? t('LOADING') + '...' : (isFromInvoice ? t('SAVE') : t('NEXT'))}
            </button>
          </div>
        </div>
      </GenericModal>
      {/* Second Modal - Figma Styled */}
      {!isFromInvoice && (
        <SecondStepModal onStartTour={() => {
          setShowSecondModal(false);
          onStartTour();
        }
        } show={showSecondModal} onClose={() => onClose()} />
      )}
    </div>
  );
};

export default CompleteProfileModal;
